import { APP_INITIALIZER, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SpeedTestModule } from 'ng-speed-test';
import { SignalREffects } from 'ngrx-signalr-core';
import { AppComponent } from './app.component';
import { BusinessErrorComponent } from './components/business-error.component';
import { DialogBodyComponent } from './components/dialogs/dialog-body.component';
import { PositionManagerComponent } from './components/partecipants/position-manager.component';
import { AppHelper } from './helpers/app.helper';
import { MaterialModule } from './material.module';
import { AppConfigService } from './services/app.config.service';
import { FELoggerService } from './services/feLogger.service';
import { I18Service } from './services/i18.service';
import { SessionService } from './services/session.service';
import { SipService } from './services/sip.service';
import { AppEffects } from './store/effects/app.effects';
import { SessionEffects } from './store/effects/session.effects';
import { SignalRHubEffects } from './store/effects/signalrhub.effects';
import { reducers } from './store/root.reducer';
// import { AgmCoreModule } from '@agm/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { GoogleMapsModule } from '@angular/google-maps';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NgWhiteboardComponent } from 'ng-whiteboard';
import { NgxColorsModule } from 'ngx-colors';
import { OverlayscrollbarsModule } from "overlayscrollbars-ngx";
import { ChatComponent } from './components/chat/chat.component';
import { NgxFlagPickerComponent } from './components/chat/ngx-flag-picker/ngx-flag-picker.component';
import { DialogAccessibilityComponent } from './components/dialog-accessibility/dialog-accessibility.component';
import { FloatingPanelComponent } from './components/floating-panel/floating-panel.component';
import { MultimediaButtonsComponent } from './components/multimedia-manager/multimedia-buttons.component';
import { MultimediaDialogComponent } from './components/multimedia-manager/multimedia-dialog.component';
import { MultimediaManagerCallerComponent } from './components/multimedia-manager/multimedia-manager-caller.component';
import { MultimediaManagerReceiverComponent } from './components/multimedia-manager/multimedia-manager-receiver.component';
import { GuestComponent } from './components/partecipants/guest/guest.component';
import { MenuComponent } from './components/partecipants/menu.component';
import { MenuCreditsComponent } from './components/partecipants/menu/credits.component';
import { MenuSettingsComponent } from './components/partecipants/menu/settings.component';
import { OperatorActionsComponent } from './components/partecipants/operator-actions.component';
import { ReceiverComponent } from './components/partecipants/primary/receiver.component';
import { RemoteInfoContentComponent } from './components/partecipants/primary/remote-info-content.component';
import { RemoteInfoComponent } from './components/partecipants/primary/remote-info.component';
import { SessionInfoContentComponent } from './components/partecipants/primary/session-info-content.component';
import { SessionInfoComponent } from './components/partecipants/primary/session-info.component';
import { CallerComponent } from './components/partecipants/secondary/caller.component';
import { StatusCircleComponent } from './components/partecipants/status-circle.component';
import { PhoneTitleComponent } from './components/phone/phone-title.component';
import { PhoneComponent } from './components/phone/phone.component';
import { CameraGalleryComponent } from './components/video/camera-gallery.component';
import { ScrollableDirective } from './components/video/scrollable.directive';
import { ThumbVideoToolbarComponent } from './components/video/thumb-video-toolbar.component';
import { ThumbVideoComponent } from './components/video/thumb-video.component';
import { VideoComponent } from './components/video/video.component';
import { CallerSnapshotComponent } from './components/video/caller-snapshot.component';
import { WhiteboardSharedComponent } from './components/video/whiteboard-shared.component';
import { WhiteboardPopupComponent } from './components/whiteboard/whiteboard-popup.component';
import { WhiteboardComponent } from './components/whiteboard/whiteboard.component';
import { DownloadDirective } from './directives/download.directive';
import { MenuTrackDirective } from './directives/menu-track.directive';
import { Ng2TelInputDirective } from './directives/ng2-tel-input.directive';
import { NgxFullscreenDirective } from './directives/ngx-fullscreen.directive';
import { ResizedDirective } from './directives/resized.directive';
import { TooltipCloseOnLeaveDirective } from './directives/tooltip-close-onleave.directive';
import { DisclaimerHelper } from './helpers/disclaimer.helper';
import { GlobalErrorHandler } from './helpers/globalErrorHandler';
import { HttpRequestInterceptor } from './helpers/httpInterceptor';
import { ActivePartecipantPipe } from './pipes/activePartecipant.pipe';
import { ConfidenceColorPipe } from './pipes/confidenceColor.pipe';
import { ConnectionSpeedPipe } from './pipes/connectionSpeed.pipe';
import { CoordinatesPipe } from './pipes/coordinates.pipe';
import { GuestChatColorPipe } from './pipes/guestChatColor.pipe';
import { IsMobilePhonePipe } from './pipes/isMobilePone.pipe';
import { IsOnCallPipe } from './pipes/isOnCall.pipe';
import { PermissionStatusTooltipPipe } from './pipes/permissionStatusTooltip.pipe';
import { RemainingTimePipe } from './pipes/remainingTime.pipe';
import { SafeHtmlPipe } from './pipes/safeHtml.pipe';
import { SafeResourceUrlPipe } from './pipes/safeResourceUrl.pipe';
import { StatusColorPipe } from './pipes/statusColor.pipe';
import { TargetPresencePipe } from './pipes/targetPresence.pipe';
import { TextMessageIconPipe } from './pipes/textMessageIcon.pipe';
import { TranslateRolePipe } from './pipes/translateRole.pipe';
import { IsRtlPipe } from './pipes/isRtl.pipe';
import { CheckerService } from './services/checker.service';
import { EventService } from './services/event.service';
import { GeoCastingService } from './services/geocasting.service';
import { ImageAnalyzer } from './services/imageAnalyzer.service';
import { NotificationService } from './services/notification.service';
import { TextTranslationService } from './services/textTranslation.service';
import { SessionStatusEffects } from './store/effects/sessionStatus.effects';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ChatPreviewComponent } from './components/chat/chat-preview.component';
import { MediaPreviewDialogComponent } from './components/dialogs/media-preview-dialog.component';
import { WhiteboardPropertiesMenuComponent } from './components/whiteboard/whiteboard-properties.component';
import { CloseCurrentCallPipe } from './pipes/closeCurrentCall.pipe';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  console.log(`translation HttpLoaderFactory`)
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${AppHelper.getBuild()}`);
}
export function initializeApp(appConfig: AppConfigService) {
  return () => appConfig.load();
}

@NgModule({
  declarations: [
    SafeHtmlPipe,
    TextMessageIconPipe,
    TranslateRolePipe,
    GuestChatColorPipe,
    ConnectionSpeedPipe,
    RemainingTimePipe,
    CoordinatesPipe,
    SafeResourceUrlPipe,
    StatusColorPipe,
    ConfidenceColorPipe,
    IsOnCallPipe,
    CloseCurrentCallPipe,
    ActivePartecipantPipe,
    IsMobilePhonePipe,
    TargetPresencePipe,
    PermissionStatusTooltipPipe,
    IsRtlPipe,
    AppComponent,
    BusinessErrorComponent,
    DialogBodyComponent,
    CallerComponent,
    MultimediaManagerCallerComponent,
    PositionManagerComponent,
    DialogAccessibilityComponent,
    MultimediaDialogComponent,
    ReceiverComponent,
    RemoteInfoContentComponent,
    SessionInfoComponent,
    SessionInfoContentComponent,
    RemoteInfoComponent,
    SessionInfoComponent,
    MultimediaManagerReceiverComponent,
    MultimediaButtonsComponent,
    ChatComponent,
    ChatPreviewComponent,
    MediaPreviewDialogComponent,
    CameraGalleryComponent,
    VideoComponent,
    CallerSnapshotComponent,
    ThumbVideoComponent,
    ThumbVideoToolbarComponent,
    WhiteboardSharedComponent,
    PhoneTitleComponent,
    GuestComponent,
    OperatorActionsComponent,
    StatusCircleComponent,
    MenuComponent,
    ScrollableDirective,
    Ng2TelInputDirective,
    PhoneComponent, //TODO probabilmente inutile!!
    WhiteboardComponent,
    WhiteboardPropertiesMenuComponent,
    WhiteboardPopupComponent,
    FloatingPanelComponent,
    MenuSettingsComponent,
    MenuCreditsComponent,
    NgxFlagPickerComponent,
    DownloadDirective,
    NgxFullscreenDirective,
    ResizedDirective,
    MenuTrackDirective,
    TooltipCloseOnLeaveDirective
  ],
  imports: [
    BrowserModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    // NgxMatIntlTelInputComponent,
    StoreModule.forRoot( reducers ), 
    EffectsModule.forRoot([AppEffects, SessionEffects, SessionStatusEffects, SignalREffects, SignalRHubEffects]),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() , connectInZone: true}),
    TranslateModule.forRoot(
      {
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          // useClass: LazyTranslateLoader,
          deps: [HttpClient]
        }
      }
    ),
    // AgmCoreModule.forRoot({
    //   apiKey: 'AIzaSyBaucKQF8G5k54fp1v51gGeN3V1tUDt3NM' // di iLocate
    // }),
    GoogleMapsModule, 
    HttpClientModule,
    BrowserAnimationsModule,
    DragDropModule,
    SpeedTestModule,
    OverlayscrollbarsModule,
    NgWhiteboardComponent,
    NgxColorsModule
  ],
  providers: [    
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfigService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
    { provide: MAT_BOTTOM_SHEET_DATA, useValue: undefined},
    { provide: MatBottomSheetRef, useValue: undefined},
    DatePipe,
    DecimalPipe,
    TranslateService,
    StatusColorPipe,
    IsOnCallPipe,
    TextMessageIconPipe,
    CloseCurrentCallPipe,
    TargetPresencePipe,
    PermissionStatusTooltipPipe,
    SessionService,
    SipService,
    FELoggerService,
    I18Service,
    GeoCastingService,
    NotificationService,
    DisclaimerHelper,
    EventService,
    CheckerService,
    TextTranslationService,
    DownloadDirective,
    Ng2TelInputDirective,
    NgxFullscreenDirective,
    ResizedDirective,
    TooltipCloseOnLeaveDirective,
    ImageAnalyzer
    // PhoneComponent,

  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {
    // [PERF-LOAD-05] AppModule Constructor
    const perfNow = performance.now();
    const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
    console.log(`[PERF-LOAD-05] AppModule Constructor | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);
  }
  // constructor(private readonly iconRegistry: MatIconRegistry) {
  //   const defaultFontSetClasses = iconRegistry.getDefaultFontSetClass();
  //   const outlinedFontSetClasses = defaultFontSetClasses
  //     .filter((fontSetClass) => fontSetClass !== 'material-icons')
  //     .concat(['material-symbols-outlined']);
  //   iconRegistry.setDefaultFontSetClass(...outlinedFontSetClasses);
  // }
 }
